import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';

import getDeviceId from '@/helpers/getDeviceId';
import { USER_QUERY_KEY } from '@/queries/users';
import { unsetUserData } from '@/redux/slices/user/actions';
import { buildURL, post } from '@/shared/fetch';
import { clearPostAuthDestination } from '@/shared/postAuthDestination';

interface LogoutOptions {
  /**
   * Where to go afterwards, or `false` to stay put. The Speedy Flow needs `false`: it lives in
   * redux rather than in the route, so navigating would close it — and it logs people out in the
   * middle of a journey it intends to continue.
   */
  redirect?: string | false;
  /**
   * Keep the saved post-auth destination. Abandoning a half-made account is not abandoning where
   * the visitor was trying to get to, and they are about to make another one to get there.
   */
  keepDestination?: boolean;
}

const useLogout = (onLogout?: () => void, { redirect = '/login', keepDestination = false }: LogoutOptions = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { mutate: logout } = useMutation({
    mutationFn: () => post(buildURL('/users/logout'), { deviceId: getDeviceId() }),
    onSuccess: () => {
      onLogout?.();
      localStorage.removeItem('giftModal');
      if (!keepDestination) {
        clearPostAuthDestination();
      }
      // The session is gone, so the cached answer about who holds it goes too. Without this the
      // query and redux disagree until something happens to refetch — and the query is the one
      // `UserSessionSync` treats as the truth.
      queryClient.setQueryData(USER_QUERY_KEY, null);
      dispatch(unsetUserData());
      if (redirect) {
        route(redirect);
      }
    },
  });

  return useCallback(() => logout(), [logout]);
};

export default useLogout;
